import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { AppAuthContextProvider, disabledAuthValue } from "./auth-core.context";
import { getRuntimeConfig } from "./auth-core.runtime";
import type { AuthContextValue, SignInReturnHandler } from "./auth-core.types";

interface AuthSessionProviderProps {
  children: ReactNode;
  authEnabled: boolean;
  /** Where the app goes after Auth0's callback; see SignInReturnHandler. */
  onSignInReturn: SignInReturnHandler;
  registerTokenGetter?: (getter: () => Promise<string>) => void;
  unregisterTokenGetter?: () => void;
}

interface Auth0ContextBridgeProps {
  children: ReactNode;
  registerTokenGetter?: (getter: () => Promise<string>) => void;
  unregisterTokenGetter?: () => void;
}

function AuthConfigError() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        padding: "2rem",
        textAlign: "center",
      }}
    >
      <div>
        <h2>Auth0 Configuration Error</h2>
        <p>Missing required environment variables: AUTH0_DOMAIN and AUTH0_CLIENT_ID</p>
        <p>Please check your .env file and restart the application.</p>
      </div>
    </div>
  );
}

/** Wires Auth0 to app auth context when auth is enabled. */
export function AuthSessionProvider({
  children,
  authEnabled,
  onSignInReturn,
  registerTokenGetter,
  unregisterTokenGetter,
}: AuthSessionProviderProps) {
  if (!authEnabled) {
    return <AppAuthContextProvider value={disabledAuthValue}>{children}</AppAuthContextProvider>;
  }

  const runtimeConfig = getRuntimeConfig();
  const authConfig = runtimeConfig.auth0;
  const domain = authConfig?.domain;
  const clientId = authConfig?.clientId;
  const callbackUrl = authConfig?.callbackUrl || window.location.origin;
  const audience = authConfig?.audience;

  if (!domain || !clientId) {
    console.error("Auth0 configuration missing. Required: AUTH0_DOMAIN and AUTH0_CLIENT_ID");
    return <AuthConfigError />;
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: callbackUrl,
        ...(audience && { audience }),
      }}
      cacheLocation="memory"
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
      onRedirectCallback={(appState) => onSignInReturn(appState?.returnTo)}
    >
      <Auth0ContextBridge
        registerTokenGetter={registerTokenGetter}
        unregisterTokenGetter={unregisterTokenGetter}
      >
        {children}
      </Auth0ContextBridge>
    </Auth0Provider>
  );
}

function Auth0ContextBridge({
  children,
  registerTokenGetter,
  unregisterTokenGetter,
}: Auth0ContextBridgeProps) {
  const auth0 = useAuth0();

  useEffect(() => {
    if (!registerTokenGetter && !unregisterTokenGetter) return;
    if (auth0.isAuthenticated && registerTokenGetter) {
      registerTokenGetter(auth0.getAccessTokenSilently);
    }
    return () => {
      unregisterTokenGetter?.();
    };
  }, [
    auth0.isAuthenticated,
    auth0.getAccessTokenSilently,
    registerTokenGetter,
    unregisterTokenGetter,
  ]);

  const value: AuthContextValue = {
    isEnabled: true,
    isAuthenticated: auth0.isAuthenticated,
    isLoading: auth0.isLoading,
    user: auth0.user,
    error: auth0.error,
    logout: auth0.logout,
    loginWithRedirect: auth0.loginWithRedirect,
    getAccessTokenSilently: auth0.getAccessTokenSilently,
    getIdTokenClaims: async () =>
      (await auth0.getIdTokenClaims()) as Record<string, unknown> | undefined,
  };

  return <AppAuthContextProvider value={value}>{children}</AppAuthContextProvider>;
}
